import { Card, Col, Divider, Row, Space, Switch, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Marker, MapContainer, TileLayer, Popup } from "react-leaflet";
import L from "leaflet";
import { motion } from "framer-motion";
import {
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined,
  ShopOutlined,
} from "@ant-design/icons";

import marker from "../test/marker.js";
import "leaflet/dist/leaflet.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import requets from "../api/index.js";
import { useTranslation } from "react-i18next";
import { useAppAuth } from "../context/use-app-auth";

const { Text } = Typography;

// ── Module-level icons — never recreated on render ────────
const locationIcon = L.divIcon({
  className: "",
  html: '<span class="map-pin map-pin--location"></span>',
  iconSize: [28, 34],
  iconAnchor: [14, 34],
  popupAnchor: [0, -36],
});

const deviceIcons = {
  ok: L.divIcon({
    className: "",
    html: '<span class="map-pin map-pin--device map-pin--device-ok"></span>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -12],
  }),
  fail: L.divIcon({
    className: "",
    html: '<span class="map-pin map-pin--device map-pin--device-fail"></span>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -12],
  }),
  inactive: L.divIcon({
    className: "",
    html: '<span class="map-pin map-pin--device map-pin--device-inactive"></span>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -12],
  }),
};

// ── Utilities ─────────────────────────────────────────────
const getDeviceStatus = (d) => {
  if (!d.isActive) return "inactive";
  if (d.status && d.status !== "OK") return "fail";
  return "ok";
};

const parseCoords = (coords) => {
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const lat = parseFloat(coords[0]);
  const lng = parseFloat(coords[1]);
  if (isNaN(lat) || isNaN(lng)) return null;
  return [lat, lng];
};

// ── Framer Motion variants ────────────────────────────────
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const mapVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: 0.38, ease: [0.22, 1, 0.36, 1] },
  },
};

// ── Count-up animation hook ───────────────────────────────
function useCountUp(target, duration = 900) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target) {
      setCount(0);
      return;
    }
    let frame = 0;
    const totalFrames = Math.round(duration / 16);
    const timer = setInterval(() => {
      frame++;
      setCount(Math.round(target * Math.min(frame / totalFrames, 1)));
      if (frame >= totalFrames) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
}

// ── Legend item ───────────────────────────────────────────
function LegendItem({ color, shape, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      {shape === "pin" ? (
        <span
          style={{
            width: 11,
            height: 11,
            background: color,
            borderRadius: "50% 50% 50% 0",
            transform: "rotate(-45deg)",
            display: "inline-block",
            flexShrink: 0,
            boxShadow: `0 1px 4px ${color}55`,
          }}
        />
      ) : (
        <span
          style={{
            width: 11,
            height: 11,
            background: color,
            borderRadius: "50%",
            border: "2px solid #fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
            display: "inline-block",
            flexShrink: 0,
          }}
        />
      )}
      <span style={{ fontSize: 12, color: "rgba(0,0,0,0.5)" }}>{label}</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────
export default function Home() {
  const auth = useAppAuth();
  const req = useMemo(() => requets(auth.user?.access_token), [auth.user?.access_token]);
  const controlPanel = useSelector((state) => state.controlPanel);
  const { t } = useTranslation("global");

  const [locations, setLocations] = useState([]);
  const [devices, setDevices] = useState([]);
  const [showLocations, setShowLocations] = useState(true);
  const [showDevices, setShowDevices] = useState(true);

  useEffect(() => {
    const { department, county, location, devEui } = controlPanel;
    Promise.all([
      req.get("/locations"),
      req.get("/device", { department, county, location, devEui }),
    ])
      .then(([locRes, devRes]) => {
        const locRaw = Array.isArray(locRes.data)
          ? locRes.data
          : locRes.data?.data || [];
        setLocations(
          locRaw
            .filter((l) => Array.isArray(l.coordenates) && l.coordenates.length >= 2)
            .map((loc) => ({
              ...loc,
              coordenates: [parseFloat(loc.coordenates[0]), parseFloat(loc.coordenates[1])],
            })),
        );

        const devRaw = Array.isArray(devRes.data)
          ? devRes.data
          : devRes.data?.data || [];
        setDevices(devRaw);
      })
      .catch(() => {});
  }, [controlPanel, req]);

  const activeLocations = locations.filter((l) => l.isActive === true);
  const mappableDevices = devices.filter((d) => parseCoords(d.coordinates) !== null);
  const activeDevices = devices.filter((d) => d.isActive === true);
  const failedDevices = activeDevices.filter((d) => d.status && d.status !== "OK");

  const cTotalLoc = useCountUp(locations.length);
  const cActiveLoc = useCountUp(activeLocations.length);
  const cTotalDev = useCountUp(devices.length);
  const cActiveDev = useCountUp(activeDevices.length);
  const cFailedDev = useCountUp(failedDevices.length);

  const stats = [
    {
      title: t("home.statLocations"),
      value: cTotalLoc,
      sub: `${cActiveLoc} ${t("home.statActive")}`,
      icon: <ShopOutlined />,
      color: "#1677ff",
      bg: "#e6f4ff",
    },
    {
      title: t("home.statDevices"),
      value: cTotalDev,
      icon: <ApiOutlined />,
      color: "#722ed1",
      bg: "#f9f0ff",
    },
    {
      title: t("home.statActiveDevices"),
      value: cActiveDev,
      icon: <CheckCircleOutlined />,
      color: "#52c41a",
      bg: "#f6ffed",
    },
    {
      title: t("home.statFailedDevices"),
      value: cFailedDev,
      icon: <CloseCircleOutlined />,
      color: "#ff4d4f",
      bg: "#fff2f0",
    },
  ];

  return (
    <div className="layout-content">
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {stats.map((stat, i) => (
          <Col xs={24} sm={12} md={12} lg={6} key={i}>
            <motion.div
              custom={i}
              initial="hidden"
              animate="visible"
              variants={cardVariants}
              style={{ height: "100%" }}
            >
              <Card
                bordered={false}
                style={{
                  borderRadius: 12,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  height: "100%",
                }}
                styles={{ body: { padding: "20px 24px" } }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 12,
                      background: stat.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 22,
                      color: stat.color,
                      flexShrink: 0,
                    }}
                  >
                    {stat.icon}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "rgba(0,0,0,0.45)",
                        marginBottom: 2,
                        lineHeight: 1.4,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                      }}
                    >
                      {stat.title}
                    </div>
                    <div
                      style={{
                        fontSize: 32,
                        fontWeight: 700,
                        color: stat.color,
                        lineHeight: 1.1,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {stat.value}
                    </div>
                    {stat.sub && (
                      <div style={{ fontSize: 11, color: "rgba(0,0,0,0.35)", marginTop: 3 }}>
                        {stat.sub}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          </Col>
        ))}
      </Row>

      <motion.div initial="hidden" animate="visible" variants={mapVariants}>
        <Card
          title={t("home.map")}
          bordered={false}
          style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
          extra={
            <Space size={16} wrap>
              <Space size={6}>
                <Switch size="small" checked={showLocations} onChange={setShowLocations} />
                <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
                  {t("home.layerLocations")}
                </Text>
              </Space>
              <Space size={6}>
                <Switch size="small" checked={showDevices} onChange={setShowDevices} />
                <Text style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
                  {t("home.layerDevices")}
                </Text>
              </Space>
            </Space>
          }
        >
          <MapContainer
            center={marker[0].coordinates}
            zoom={14}
            scrollWheelZoom={true}
            style={{ height: "600px", width: "100%", borderRadius: 8 }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {showLocations && (
              <MarkerClusterGroup chunkedLoading maxClusterRadius={40} animate>
                {activeLocations.map((loc) => (
                  <Marker key={loc.uuid} position={loc.coordenates} icon={locationIcon}>
                    <Popup minWidth={260}>
                      <div style={{ padding: "4px 0" }}>
                        {loc.locationImage && (
                          <img
                            alt={loc.name}
                            src={loc.locationImage}
                            style={{
                              width: "100%",
                              height: 110,
                              objectFit: "cover",
                              borderRadius: 6,
                              marginBottom: 10,
                              display: "block",
                            }}
                          />
                        )}
                        <Text strong style={{ fontSize: 13, display: "block", marginBottom: 4 }}>
                          {loc.name}
                        </Text>
                        {loc.description && (
                          <Text
                            type="secondary"
                            style={{ fontSize: 12, display: "block", marginBottom: 6 }}
                          >
                            {loc.description}
                          </Text>
                        )}
                        <Divider style={{ margin: "6px 0" }} />
                        <div
                          style={{
                            fontSize: 11,
                            color: "rgba(0,0,0,0.45)",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <EnvironmentOutlined />
                          <span>
                            {loc.coordenates[0].toFixed(5)}, {loc.coordenates[1].toFixed(5)}
                          </span>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            )}

            {showDevices && (
              <MarkerClusterGroup chunkedLoading maxClusterRadius={30} animate>
                {mappableDevices.map((device) => {
                  const coords = parseCoords(device.coordinates);
                  const status = getDeviceStatus(device);
                  return (
                    <Marker key={device.uuid} position={coords} icon={deviceIcons[status]}>
                      <Popup minWidth={220}>
                        <div style={{ padding: "4px 0" }}>
                          <Text
                            strong
                            style={{ fontSize: 13, display: "block", marginBottom: 3 }}
                          >
                            {device.name}
                          </Text>
                          <code
                            style={{
                              fontSize: 11,
                              color: "#1677ff",
                              display: "block",
                              marginBottom: 8,
                            }}
                          >
                            {device.devEui}
                          </code>
                          <Divider style={{ margin: "6px 0" }} />
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <Tag
                              color={
                                status === "ok"
                                  ? "success"
                                  : status === "fail"
                                    ? "error"
                                    : "default"
                              }
                              style={{ margin: 0, fontSize: 11 }}
                            >
                              {t(`home.device${status.charAt(0).toUpperCase()}${status.slice(1)}`)}
                            </Tag>
                            {device.application && (
                              <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                                {device.application}
                              </Tag>
                            )}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MarkerClusterGroup>
            )}
          </MapContainer>

          <div
            style={{
              display: "flex",
              gap: 20,
              marginTop: 12,
              flexWrap: "wrap",
              paddingLeft: 2,
            }}
          >
            <LegendItem color="#1677ff" shape="pin" label={t("home.layerLocations")} />
            <LegendItem color="#52c41a" shape="dot" label={t("home.deviceOk")} />
            <LegendItem color="#ff4d4f" shape="dot" label={t("home.deviceFail")} />
            <LegendItem color="#bfbfbf" shape="dot" label={t("home.deviceInactive")} />
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
